import { useState, type FormEvent, type ReactNode } from 'react';
import { useSearch } from '@tanstack/react-router';
import { SignOut } from '@phosphor-icons/react';
import { useNavigate } from '../lib/routing';
import { useAuth } from '../hooks/use-auth';
import { authClient } from '../lib/auth-client';
import { api } from '../lib/api';
import { isStrongPassword, PASSWORD_TOO_SHORT_MESSAGE } from '../lib/password';
import PasswordChecklist from '../components/password-checklist';
import Header from '../components/header';
import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export default function AdminInviteAccept() {
  const search = useSearch({ strict: false }) as Record<string, string>;
  const token = search.token ?? '';
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!authLoading && user) {
    const handleSignOut = async () => {
      await authClient.signOut();
      void navigate('/sign-in');
    };

    return (
      <>
        <Header />
        <main className="max-w-[480px] mx-auto mt-10 px-6 pb-16">
          <Card>
            <h1 className="mt-0 text-[22px] text-foreground">Activation du compte</h1>
            <Alert className="mt-2 mb-5">
              Vous êtes connecté·e en tant que <strong>{user.email}</strong>.
              Déconnectez-vous d&apos;abord pour activer ce compte.
            </Alert>
            <Button
              type="button"
              onClick={() => void handleSignOut()}
              className="w-full min-h-[44px] gap-2"
            >
              <SignOut size={20} />
              Se déconnecter
            </Button>
          </Card>
        </main>
      </>
    );
  }

  if (!token) {
    return (
      <>
        <Header />
        <main className="max-w-[480px] mx-auto mt-10 px-6 pb-16">
          <Card>
            <h1 className="mt-0 text-[22px] text-foreground">Activation du compte</h1>
            <Alert className="mt-2 mb-4">
              Lien invalide. Vérifiez le lien reçu par email.
            </Alert>
          </Card>
        </main>
      </>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!isStrongPassword(password)) {
      setError(PASSWORD_TOO_SHORT_MESSAGE);
      return;
    }
    if (password !== confirm) {
      setError('Les deux mots de passe ne correspondent pas.');
      return;
    }

    setBusy(true);
    try {
      const { email } = await api.userInvite.accept.mutate({ token, password });
      const signInResult = await authClient.signIn(email, password);
      if (!signInResult.error) {
        void navigate('/direction');
        return;
      }
      setDone(true);
      setTimeout(() => void navigate('/sign-in'), 2000);
    } catch (e2) {
      const msg = e2 instanceof Error ? e2.message : String(e2);
      if (msg.includes('INVITE_ALREADY_USED')) {
        setError('Ce lien a déjà été utilisé. Connectez-vous directement.');
      } else if (msg.includes('INVITE_EXPIRED')) {
        setError('Ce lien est expiré. Contactez votre administrateur pour un renvoi.');
      } else if (msg.includes('INVITE_INVALID')) {
        setError('Lien invalide. Vérifiez le lien reçu par email.');
      } else {
        setError('Une erreur est survenue. Réessayez ou contactez votre administrateur.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Header />
      <main className="max-w-[480px] mx-auto mt-10 px-6 pb-16">
        <Card>
          <h1 className="mt-0 text-[22px] text-foreground">Activez votre compte</h1>
          <p className="text-sm text-muted-foreground -mt-1 mb-5">
            Choisissez un mot de passe pour accéder à votre espace Naïa.
          </p>

          {done ? (
            <Alert>Compte activé ! Redirection vers la connexion…</Alert>
          ) : (
            <form onSubmit={submit} noValidate>
              <Field label="Mot de passe">
                <div className="relative">
                  <Input
                    type={showPw ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="10 car. : maj, min, chiffre, symbole"
                    disabled={busy}
                    className="bg-card pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw((v) => !v)}
                    aria-label={showPw ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    title={showPw ? 'Masquer' : 'Afficher'}
                    className="absolute right-[2px] top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-transparent border-none p-0 cursor-pointer text-[18px] leading-none"
                  >
                    {showPw ? '🙈' : '👁️'}
                  </button>
                </div>
                {password.length > 0 && <PasswordChecklist password={password} />}
              </Field>

              <Field label="Confirme le mot de passe">
                <Input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Retape-le"
                  disabled={busy}
                  className="bg-card"
                />
              </Field>

              {error && <Alert className="mt-2 mb-4">{error}</Alert>}

              <Button type="submit" disabled={busy} className="w-full mt-2 font-black text-[18px] shadow-card">
                {busy ? 'Activation…' : 'Activer mon compte'}
              </Button>
            </form>
          )}
        </Card>
      </main>
    </>
  );
}

function Card({ children }: { children: ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-card p-8 shadow-card">
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <label className="block mb-2 font-bold text-foreground text-sm">
        {label}
      </label>
      {children}
    </div>
  );
}
